/**
 * Device Session Service
 *
 * Enforces one active device per profile user. New login invalidates old devices
 * via Supabase Realtime broadcast + DB check on wake.
 *
 * Three invalidation paths:
 * - Path 1 (awake): broadcast channel receives session_invalidated
 * - Path 2 (sleeping): visibilitychange triggers DB check
 * - Path 3 (mount): App.js mount triggers DB check
 */

import { supabase } from '../supabase';
import { clearAllData } from '../storage/userDataStorage';
import { logEvent, getProfileUsername } from '../analytics/eventLogger';

const CHANNEL_PREFIX = 'session:';

/**
 * Register this device as the active session for a profile user.
 * Upserts device_sessions — one row per user, always.
 * @param {string} profileUserId
 * @returns {string} the new device_id
 */
export const registerDevice = async (profileUserId) => {
  const deviceId = crypto.randomUUID();

  const { error } = await supabase.from('device_sessions').upsert(
    {
      profile_user_id: profileUserId,
      device_id: deviceId,
      created_at: new Date().toISOString(),
      last_seen_at: new Date().toISOString(),
    },
    { onConflict: 'profile_user_id' }
  );

  if (error) {
    console.error('[DEVICE_SESSION] registerDevice failed:', error.message);
    throw error;
  }

  localStorage.setItem('device_id', deviceId);
  console.log('[DEVICE_SESSION] Registered device:', deviceId, 'for', profileUserId);
  return deviceId;
};

/**
 * Validate this device is still the active session.
 * Compares localStorage device_id against device_sessions table.
 * @param {string} profileUserId
 * @returns {boolean} true if valid, false if invalidated or not found
 */
export const validateDevice = async (profileUserId) => {
  const localDeviceId = localStorage.getItem('device_id');
  if (!localDeviceId) return false;

  const { data, error } = await supabase
    .from('device_sessions')
    .select('device_id')
    .eq('profile_user_id', profileUserId)
    .maybeSingle();

  if (error) {
    console.error('[DEVICE_SESSION] validateDevice query failed:', error.message);
    // On error, don't kick — fail open so the user isn't blocked by a transient issue
    return true;
  }

  if (!data) return false;
  return data.device_id === localDeviceId;
};

/**
 * Broadcast session_invalidated to all devices listening on this user's channel.
 * Returns a promise that resolves when Supabase acknowledges the send.
 * @param {string} profileUserId
 */
export const broadcastInvalidation = async (profileUserId) => {
  const channel = supabase.channel(`${CHANNEL_PREFIX}${profileUserId}`);

  // Subscribe first, then send
  await new Promise((resolve, reject) => {
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') resolve();
      if (status === 'CHANNEL_ERROR') reject(new Error('Channel subscribe failed'));
    });
  });

  const result = await channel.send({
    type: 'broadcast',
    event: 'session_invalidated',
    payload: { timestamp: Date.now() },
  });

  // Clean up — this device doesn't need to stay subscribed to this channel instance
  supabase.removeChannel(channel);

  if (result !== 'ok') {
    console.warn('[DEVICE_SESSION] Broadcast send returned:', result);
  }

  console.log('[DEVICE_SESSION] Broadcasted session_invalidated for:', profileUserId);
};

/**
 * Subscribe to session invalidation events for this profile user.
 * @param {string} profileUserId
 * @param {Function} onInvalidated - called with the invalidation path name
 * @returns {Function} unsubscribe function
 */
export const subscribeToInvalidation = (profileUserId, onInvalidated) => {
  const channel = supabase.channel(`${CHANNEL_PREFIX}${profileUserId}`);

  channel
    .on('broadcast', { event: 'session_invalidated' }, async () => {
      console.log('[DEVICE_SESSION] Received session_invalidated broadcast');
      // Validate before kicking — this device may have sent the broadcast itself
      // (e.g., new account creation broadcasts invalidation, then hears its own message)
      const isValid = await validateDevice(profileUserId);
      if (isValid) {
        console.log('[DEVICE_SESSION] Broadcast received but this device is still valid, ignoring');
        return;
      }
      onInvalidated('broadcast');
    })
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Handle invalidation: log event, clear local data, show message, redirect.
 * @param {string} path - which invalidation path triggered this ('broadcast' | 'visibility_check' | 'mount_check')
 */
export const handleInvalidation = async (path) => {
  console.log('[DEVICE_SESSION] Handling invalidation via:', path);
  logEvent('session_kicked', { path, profileUsername: getProfileUsername() });

  await clearAllData();

  // Redirect to root with hash so DemoGate can show the message
  // (sessionStorage is cleared by clearAllData, so we use a URL hash)
  window.location.href = '/?kicked=true';
};
